/**
 * SessionStorage client with error handling
 *
 * Provides a safe wrapper around sessionStorage that handles errors gracefully.
 * This is necessary because sessionStorage can throw errors in certain scenarios:
 * - When storage is disabled (private browsing mode)
 * - When storage quota is exceeded
 * - When storage is not available (some browsers/contexts)
 *
 * All keys are automatically prefixed with 'plume-hub:' to prevent conflicts
 * with other apps that may share the same sessionStorage.
 */

const SESSION_STORAGE_PREFIX = "plume-hub:";

/**
 * Adds the Plume prefix to a key
 */
const prefixKey = (key: string): string => {
  return `${SESSION_STORAGE_PREFIX}${key}`;
};

/**
 * Checks if sessionStorage is available
 */
const isSessionStorageAvailable = (): boolean => {
  try {
    const test = "__sessionStorage_test__";
    sessionStorage.setItem(test, test);
    sessionStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

/**
 * Safely get an item from sessionStorage
 * @param key - The key to retrieve (will be automatically prefixed with 'plume-hub:')
 * @returns The value if found, null if not found or on error
 */
export const getSessionStorageItem = (key: string): string | null => {
  if (!isSessionStorageAvailable()) {
    console.warn("SessionStorage is not available");
    return null;
  }

  try {
    const prefixedKey = prefixKey(key);
    return sessionStorage.getItem(prefixedKey);
  } catch (error) {
    console.error(`Error getting sessionStorage item "${key}":`, error);
    return null;
  }
};

/**
 * Safely set an item in sessionStorage
 * @param key - The key to set (will be automatically prefixed with 'plume-hub:')
 * @param value - The value to store
 * @returns true if successful, false on error
 */
export const setSessionStorageItem = (key: string, value: string): boolean => {
  if (!isSessionStorageAvailable()) {
    console.warn("SessionStorage is not available");
    return false;
  }

  try {
    const prefixedKey = prefixKey(key);
    sessionStorage.setItem(prefixedKey, value);
    return true;
  } catch (error) {
    console.error(`Error setting sessionStorage item "${key}":`, error);
    return false;
  }
};

/**
 * Safely remove an item from sessionStorage
 * @param key - The key to remove (will be automatically prefixed with 'plume-hub:')
 * @returns true if successful, false on error
 */
export const removeSessionStorageItem = (key: string): boolean => {
  if (!isSessionStorageAvailable()) {
    console.warn("SessionStorage is not available");
    return false;
  }

  try {
    const prefixedKey = prefixKey(key);
    sessionStorage.removeItem(prefixedKey);
    return true;
  } catch (error) {
    console.error(`Error removing sessionStorage item "${key}":`, error);
    return false;
  }
};

/**
 * Safely clear all items from sessionStorage
 * WARNING: This clears ALL sessionStorage, not just plume-hub keys
 * @returns true if successful, false on error
 */
export const clearSessionStorage = (): boolean => {
  if (!isSessionStorageAvailable()) {
    console.warn("SessionStorage is not available");
    return false;
  }

  try {
    sessionStorage.clear();
    return true;
  } catch (error) {
    console.error("Error clearing sessionStorage:", error);
    return false;
  }
};

/**
 * Safely clear only plume-hub prefixed items from sessionStorage
 * This preserves other apps' sessionStorage data
 * @returns true if successful, false on error
 */
export const clearPlumeSessionStorage = (): boolean => {
  if (!isSessionStorageAvailable()) {
    console.warn("SessionStorage is not available");
    return false;
  }

  try {
    const keysToRemove: string[] = [];
    // Iterate through all sessionStorage keys
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(SESSION_STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    // Remove all plume-hub prefixed keys
    keysToRemove.forEach((key) => {
      sessionStorage.removeItem(key);
    });
    return true;
  } catch (error) {
    console.error("Error clearing plume sessionStorage:", error);
    return false;
  }
};
